import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  WebAuthnError,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { apiClient } from './client';
import type { AuthResponse } from '../types';

// What the settings page renders for each registered key. Mirrors the API's
// CredentialSummary (dates arrive as ISO strings over JSON).
export interface PasskeySummary {
  id: string;
  nickname: string | null;
  deviceType: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

export { browserSupportsWebAuthn };

export const webauthnApi = {
  // Enroll a new passkey/security key: fetch creation options + a signed token
  // carrying the challenge, run the authenticator, post the attestation back.
  async register(nickname?: string): Promise<PasskeySummary> {
    const { data } = await apiClient.post<{
      options: PublicKeyCredentialCreationOptionsJSON;
      regToken: string;
    }>('/auth/webauthn/register/options');
    const attestation = await startRegistration({ optionsJSON: data.options });
    const verify = await apiClient.post<{ credential: PasskeySummary }>(
      '/auth/webauthn/register',
      { regToken: data.regToken, response: attestation, nickname },
    );
    return verify.data.credential;
  },

  async list(): Promise<PasskeySummary[]> {
    const { data } = await apiClient.get<{ credentials: PasskeySummary[] }>(
      '/auth/webauthn/credentials',
    );
    return data.credentials;
  },

  async rename(id: string, nickname: string): Promise<void> {
    await apiClient.patch(`/auth/webauthn/credentials/${id}`, { nickname });
  },

  async remove(id: string): Promise<void> {
    await apiClient.delete(`/auth/webauthn/credentials/${id}`);
  },

  // Second factor at login: run the assertion against the server-issued
  // challenge options, then complete the session.
  async authenticate(
    mfaToken: string,
    options: PublicKeyCredentialRequestOptionsJSON,
    rememberMe: boolean,
  ): Promise<AuthResponse> {
    const assertion = await startAuthentication({ optionsJSON: options });
    const { data } = await apiClient.post<AuthResponse>(
      '/auth/webauthn/authenticate',
      { mfaToken, response: assertion, rememberMe },
    );
    return data;
  },
};

// True when the user dismissed or aborted the native passkey prompt, so the UI
// can show a gentle "cancelled" message rather than a scary error.
export function isWebauthnCancellation(err: unknown): boolean {
  return (
    err instanceof WebAuthnError &&
    (err.code === 'ERROR_CEREMONY_ABORTED' ||
      (err.cause instanceof Error && err.cause.name === 'NotAllowedError'))
  );
}
